import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ThresholdStreakTaskCurrentThresholdState } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BrandXXL,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ScoreGaugeTarget } from '@sorare/core/src/components/lineup/ScoreGaugeTarget';

import { TargetIcon } from 'assets/icons/TargetIcon';
import { Variant } from 'components/rewards/RewardOverview/types';

import { Rewards } from '../../Rewards';
import { TutoRewards } from '../TutoRewards';
import type { DetailsDialog_thresholdStreakTask } from './__generated__/index.graphql';
import fire from './assets/fire.svg';

const Content = styled(Vertical)`
  height: 100%;
  padding: 0 var(--triple-unit) var(--triple-unit);
`;
const Lineups = styled(Vertical)`
  width: 100%;
`;
const IconWrapper = styled.div`
  position: relative;
  z-index: -1;
  bottom: 0;
  right: calc(var(--double-and-a-half-unit) * -1);
  line-height: inherit;
  transform: rotate(15deg);
  width: max-content;
  &.gray {
    filter: grayscale(1);
  }
  > * {
    vertical-align: baseline;
  }
`;
const Header = styled(Vertical)`
  padding: var(--unit) var(--triple-unit) var(--triple-unit);
`;
const Score = styled(BrandXXL)`
  position: relative;
  isolation: isolate;
  display: grid;
  text-shadow: 0 0 8px var(--c-nd-500);
  justify-items: end;
  > * {
    grid-area: 1 / 1;
  }
`;
const Streak = styled.span`
  background: var(--c-rivals-gradient-rep-boost);
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;
const TutoRewardsWrapper = styled(Vertical)`
  padding: var(--triple-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
`;
const RewardsWrapper = styled(SBHorizontal)`
  width: 100%;
`;
type Props = {
  thresholdStreakTask: DetailsDialog_thresholdStreakTask;
  onClose: () => void;
  showLive?: boolean;
};
export const DetailsDialog = ({
  thresholdStreakTask,
  showLive = true,
  onClose,
}: Props) => {
  const {
    title,
    progress,
    currentThresholdStatus,
    currentThreshold,
    rarity,
    so5LeagueTrack,
    thresholds,
  } = thresholdStreakTask;

  const [displayIndex, setDisplayIndex] = useState<number>(
    Math.min(progress, thresholds.length - 1)
  );

  const displayedThreshold = thresholds[displayIndex];

  const lineups =
    so5LeagueTrack?.so5Leaderboards.flatMap(
      ({ mySo5Lineups }) => mySo5Lineups
    ) || [];
  const isLive =
    [
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_IN_PROGRESS,
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_HIT,
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_MISSED,
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_NOT_SET,
      ThresholdStreakTaskCurrentThresholdState.COMPLETED,
    ].includesValue(currentThresholdStatus.state) &&
    lineups.length > 0 &&
    showLive;

  let displayedThresholdIcon: ReactNode;
  if (
    displayedThreshold.score === currentThreshold.score &&
    currentThresholdStatus.state ===
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_HIT
  ) {
    displayedThresholdIcon = <img src={fire} alt="" width="16" height="16" />;
  } else if (displayedThreshold.score <= currentThreshold.score) {
    displayedThresholdIcon = <TargetIcon color="var(--c-rivals-loss)" />;
  } else {
    displayedThresholdIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <g opacity="0.5">
          <path
            d="M3.21338 7.2301H12.8536V14.4603H3.21338V7.2301Z"
            stroke="white"
            strokeWidth="1.60671"
          />
          <path
            d="M11.2471 7.23028V4.82022C11.2471 3.04551 9.80844 1.60681 8.03372 1.60681V1.60681C6.25901 1.60681 4.82031 3.0455 4.82031 4.82022V7.23028"
            stroke="white"
            strokeWidth="1.60671"
          />
          <path
            d="M6.42627 10.8452H9.63968"
            stroke="white"
            strokeWidth="1.60671"
          />
        </g>
      </svg>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={
        <LabelL bold className="text-center">
          {title}
        </LabelL>
      }
    >
      <Content gap={3}>
        <Header gap={0} center>
          <Score>
            <span>{progress}</span>
            <IconWrapper className={classnames({ gray: progress === 0 })}>
              <img src={fire} alt="" width="36" height="36" />
            </IconWrapper>
          </Score>
          <LabelM>
            <FormattedMessage
              id="GameWeekly.UpcomingDialog.label"
              defaultMessage="GW Streak!"
            />
          </LabelM>
        </Header>
        {isLive ? (
          <Lineups gap={2}>
            {lineups?.map(({ so5Rankings, id, managerTeam, name }) => {
              const sections = [
                { score: 0 },
                { score: 0 },
                { score: currentThreshold.score },
              ];
              const targetPosition = 100 - 100 / (sections.length + 1);
              const maxRight = 10;
              const currentScore = so5Rankings.at(0)?.score || 0;
              return (
                <ScoreGaugeTarget
                  key={id}
                  name={managerTeam?.name || name}
                  sections={sections}
                  currentScore={currentScore}
                  cursorPosition={`${Math.min(Math.max(100 - (currentScore / currentThreshold.score) * targetPosition, maxRight), 100)}%`}
                />
              );
            })}
          </Lineups>
        ) : (
          <TutoRewardsWrapper className="text-center" center gap={3}>
            <TutoRewards
              thresholdStreakTask={thresholdStreakTask}
              displayIndex={displayIndex}
              setDisplayIndex={setDisplayIndex}
            />
            <BodyM bold>
              <FormattedMessage
                id="GameWeekly.UpcomingDialog.description"
                defaultMessage="Your <bold>streak</bold> counts how many Game Weeks in a row you've hit the target."
                values={{
                  bold: c => <Streak>{c}</Streak>,
                }}
              />
            </BodyM>
          </TutoRewardsWrapper>
        )}
        <RewardsWrapper>
          <Horizontal gap={0.5}>
            {displayedThresholdIcon}
            <LabelL bold>
              <FormattedMessage
                id="GameWeekly.UpcomingDialog.reward"
                defaultMessage="Streak Reward"
              />
            </LabelL>
          </Horizontal>
          <Horizontal>
            <Rewards
              rewardConfigs={displayedThreshold.rewardConfigs}
              variant={Variant.CLASSIC}
              rarity={rarity}
            />
          </Horizontal>
        </RewardsWrapper>
      </Content>
    </Dialog>
  );
};

DetailsDialog.fragments = {
  thresholdStreakTask: gql`
    fragment DetailsDialog_thresholdStreakTask on ThresholdsStreakTask {
      id
      title
      progress
      rarity
      currentThresholdStatus {
        state
      }
      currentThreshold {
        score
      }
      thresholds {
        score
        rewardConfigs {
          id
          ...Rewards_anyRewardConfig
        }
      }
      so5LeagueTrack {
        slug
        so5Leaderboards {
          slug
          mySo5Lineups {
            id
            name
            managerTeam {
              id
              name
            }
            so5Rankings {
              id
              score
            }
          }
        }
      }
      ...TutoRewards_thresholdStreakTask
    }
    ${TutoRewards.fragments.thresholdStreakTask}
    ${Rewards.fragments.anyRewardConfig}
  ` as TypedDocumentNode<DetailsDialog_thresholdStreakTask>,
};
